import type {
  DefaultRecordValueConfigurationFilterType,
  DefaultRecordValueConfigurationType,
} from "../schemas/DefaultRecordValueConfigurationSchemas.ts";
import type { JobStatusType, JobType } from "../schemas/JobSchemas.ts";
import type {
  CreateOneRequestType,
  DeleteOneRequestType,
  FilterObjectType,
  GetListResponseType,
  PaginatedListRequestType,
  ResourceAdapterType,
} from "../util/index.ts";
import { getFilterObject } from "../util/index.ts";
import { restAPI } from "./api.ts";
import type { AssociatedAssetFilterType } from "./RecordDomain/index.ts";

export type DefaultRecordValueConfigurationLookupType = { drvcId: string };

export type DefaultRecordValueConfigurationServiceType = Pick<
  ResourceAdapterType<DefaultRecordValueConfigurationType>,
  "createOne" | "deleteOne"
>;

export type DefaultRecordValueConfigurationApplyAsyncResponseType = {
  status: JobStatusType;
  job: JobType;
};

export type DefaultRecordValueConfigurationApplyFilterType =
  DefaultRecordValueConfigurationFilterType &
    FilterObjectType<{
      year_month: { $in: string[] };
    }> &
    AssociatedAssetFilterType;

export const DefaultRecordValueConfigurationDomain = {
  /** Create a default record value configuration
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/default_record_value_configuration/upsert_default_record_value_configuration
   */
  createOne: ({
    data,
  }: CreateOneRequestType<DefaultRecordValueConfigurationType>) =>
    restAPI.nodeAPI
      .POST<DefaultRecordValueConfigurationType>({
        endpoint: `/default_record_value_configurations`,
        body: data,
      })
      .then((resp) => ({ data: resp })),

  /** Delete a default record value configuration
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/default_record_value_configuration/delete_default_record_value_configuration
   */
  deleteOne: ({
    id,
  }: DeleteOneRequestType<DefaultRecordValueConfigurationType>) =>
    restAPI.nodeAPI
      .DELETE<DefaultRecordValueConfigurationType>({
        endpoint: `/default_record_value_configurations/${id}`,
      })
      .then(() => ({ success: true })),

  getList: async ({
    page,
    pageSize,
    sortBy,
    sortDirection,
    groupBy,
    filters,
    ...restProps
  }: PaginatedListRequestType<DefaultRecordValueConfigurationFilterType>) => {
    const formattedFilters = getFilterObject(filters);

    return restAPI.nodeAPI.POST<
      GetListResponseType<DefaultRecordValueConfigurationType>
    >({
      endpoint: "/default_record_value_configurations/search",
      body: {
        ...restProps,
        page,
        page_size: pageSize,
        sort_by: sortBy,
        sort_direction: sortDirection,
        filter: formattedFilters,
        group_by: groupBy,
      },
    });
  },

  getOne: async ({ drvcId }: DefaultRecordValueConfigurationLookupType) =>
    restAPI.nodeAPI.GET<DefaultRecordValueConfigurationType>({
      endpoint: `/default_record_value_configurations/${drvcId}`,
    }),

  applyDefaultConfigurationAsync: (
    filters?: DefaultRecordValueConfigurationApplyFilterType,
    overwriteAllValues?: boolean
  ) =>
    restAPI.nodeAPI.POST<DefaultRecordValueConfigurationApplyAsyncResponseType>(
      {
        endpoint: `/default_record_value_configurations/apply_async`,
        body: {
          filter: filters ? getFilterObject(filters) : {},
          overwrite_all_values: !!overwriteAllValues,
        },
      }
    ),
};
